import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ensurePack, flush } from "@/lib/fieldSync";
import { kvGet, pendingCount } from "@/lib/fieldStore";

const Ctx = createContext(null);
export const useFieldSync = () => useContext(Ctx);

// Stato di connessione, pacchetto per lavorare senza rete e sincronizzazione, condivisi da tutte le schermate dell'auditor.
export function FieldSyncProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState(null);
  const [pack, setPack] = useState(null);
  const [tick, setTick] = useState(0); // cambia a ogni sincronizzazione: le schermate rileggono la copia locale
  const busy = useRef(false);
  const again = useRef(false);
  const debounce = useRef(null);

  const refreshPending = useCallback(async () => { try { setPending(await pendingCount()); } catch { /* archivio non disponibile */ } }, []);

  const sync = useCallback(async () => {
    if (busy.current) { again.current = true; return; }
    busy.current = true;
    setSyncing(true);
    try {
      if (navigator.onLine !== false) {
        const p = await ensurePack();
        if (p) setPack((cur) => (cur && cur.hash === p.hash ? cur : p));
      }
      const res = await flush(() => { setTick((t) => t + 1); });
      setError(res.ok ? null : res.error);
      if (res.ok) setLastSync(new Date());
      if (res.problems) toast.error(`${res.problems} elemento/i non accettati dal server: guarda l'avviso nel sopralluogo.`);
    } finally {
      busy.current = false;
      setSyncing(false);
      setTick((t) => t + 1);
      await refreshPending();
      if (again.current) { again.current = false; setTimeout(() => sync(), 300); }
    }
  }, [refreshPending]);

  // dopo ogni modifica: si prova a inviare subito (se non c'e' rete resta in coda e parte da sola)
  const kick = useCallback(() => {
    refreshPending();
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { if (navigator.onLine !== false) sync(); }, 1200);
  }, [sync, refreshPending]);

  useEffect(() => {
    kvGet("pack").then((p) => { if (p) setPack(p); }).catch(() => null);
    const on = () => { setOnline(true); sync(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    refreshPending();
    if (navigator.onLine !== false) sync();
    const timer = setInterval(() => { if (navigator.onLine !== false) sync(); }, 30000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); clearInterval(timer); clearTimeout(debounce.current); };
  }, [sync, refreshPending]);

  const value = useMemo(() => ({ online, pending, syncing, lastSync, error, pack, tick, sync, kick }),
    [online, pending, syncing, lastSync, error, pack, tick, sync, kick]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// "Salvato sul dispositivo" oppure "sincronizzato": sempre chiaro dove sono i dati.
export function SyncBadge() {
  const { online, pending, syncing, lastSync, pack } = useFieldSync();
  let text, cls;
  if (pending > 0) { text = online ? (syncing ? "Invio…" : `Salvato sul tablet · ${pending} da inviare`) : `Offline · salvato sul tablet (${pending})`; cls = "bg-[#FBEFD2] text-[#8A5A00] border-[#EAD9A8]"; }
  else if (!online) { text = pack ? "Offline · tutto salvato sul tablet" : "Offline · apri l'app con la rete almeno una volta"; cls = "bg-white text-[#5C6667] border-[#D9CBB4]"; }
  else { text = lastSync ? "Tutto inviato" : "Connesso"; cls = "bg-[#DDF0E5] text-[#1F6B45] border-[#B8DEC8]"; }
  return <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold ${cls}`} role="status" data-testid="sync-badge">{text}</span>;
}
